/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import {
    ActionTagApi,
    Configuration,
    ConfigurationParameters,
    ContentActionsGetRequest,
    ContentScreenDetails,
    ContentScreenDetailsContentGetRequest,
    ContentScreenDetailsOfferGetRequest,
    ContentScreenDetailsRecordingGetRequest,
    OfferActionsGetRequest,
    PlayNextContentActionsGetRequest,
    PlayNextRecordingActionsGetRequest,
    UnifiedCollectionActionsGetRequest,
    UnifiedCollectionScreenDetailsGetRequest,
} from 'client-code/actions';
import { ScreenType } from 'client-code/previews';

import { CommonHeaders } from '../CommonHeaders';
import { Constants } from '../constants';
import { IActionsServiceRepository } from '../interfaces';
import { Log } from '../logging';
import { ActionsServiceV1Contract } from '../servicecontracts';
import {
    TActionList,
    TActionsContentParams,
    TActionsContentScreenContentIdParams,
    TActionsContentScreenOfferParams,
    TActionsContentScreenRecordingParams,
    TActionsParams,
    TActionsParamsUCScreenDetails,
    TActionsRecordingParams,
    TActionsUnifiedCollectionParams,
    TActionsVodOfferParams,
    TCustomActionList,
    TPlayNextContentActionsParams,
    TPlayNextRecordingActionsParams,
    TServicesParams,
    TUnifiedCollectionScreenDetails,
} from '../types';
import { UniqueIdentifier } from '../utils';

export class ActionsServiceRepositoryImpl
    implements IActionsServiceRepository<ActionsServiceV1Contract>
{
    private static TAG: string = 'ActionsServiceRepositoryImpl';

    private mParams: TServicesParams;

    public constructor(params: TServicesParams) {
        this.mParams = params;
    }

    private getActionsApi(commonHeaders: CommonHeaders): ActionTagApi {
        const configurationParameters = this.getConfigurationParameters(commonHeaders);
        const configuration = new Configuration(configurationParameters);
        return new ActionTagApi(configuration);
    }

    private getCommonRequestParameters(commonHeaders: CommonHeaders) {
        const {
            ApplicationName,
            DeviceType,
            ProductName,
            ApplicationFeatureArea,
            ApplicationVersion,
        } = commonHeaders.headers;
        return {
            applicationName: ApplicationName,
            deviceType: DeviceType,
            productName: ProductName,
            acceptLanguage: commonHeaders.headers['Accept-Language'],
            applicationFeatureArea: ApplicationFeatureArea,
            applicationVersion: ApplicationVersion,
            timeZoneName: this.mParams.timeZoneName,
            bodyId: this.mParams.bodyId,
            xAmznRequestId: UniqueIdentifier.generateXAmznRequestId(),
            whiny: this.mParams.whiny || 'false',
        };
    }

    private getConfigurationParameters(commonHeaders: CommonHeaders) {
        const configurationParameters: ConfigurationParameters = {
            apiKey: this.mParams.accessToken,
            basePath: this.mParams.basePath,
            baseOptions: {
                timeout: Constants.SERVICE_CONNECTION_TIMEOUT,
                headers: commonHeaders.headers,
            },
        };
        return configurationParameters;
    }

    private async getContentScreenDetailsRecordingActionsFromService(
        actionsParams: TActionsContentScreenRecordingParams,
    ): Promise<ContentScreenDetails> {
        try {
            const { recordingId, channelNumber } = actionsParams;
            const commonHeaders = new CommonHeaders(ScreenType.ActionScreenEpisodeItem);
            const requestContentScreenDetailsParameters: ContentScreenDetailsRecordingGetRequest = {
                recordingId,
                channelNumber,
                ...this.getCommonRequestParameters(commonHeaders),
            };
            const contentScreenDetailsRecordingResponse = await this.getActionsApi(
                commonHeaders,
            ).contentScreenDetailsRecordingGet(requestContentScreenDetailsParameters);
            return contentScreenDetailsRecordingResponse.data;
        } catch (error) {
            Log.e(
                ActionsServiceRepositoryImpl.TAG,
                'getContentScreenDetailsRecordingActionsFromService()',
                error,
            );
            throw error;
        }
    }

    private async getContentScreenDetailsOfferActionsFromService(
        actionsParams: TActionsContentScreenOfferParams,
    ): Promise<ContentScreenDetails> {
        try {
            const { offerId, channelNumber } = actionsParams;
            const commonHeaders = new CommonHeaders(ScreenType.ActionScreenEpisodeItem);
            const requestContentScreenDetailsParameters: ContentScreenDetailsOfferGetRequest = {
                offerId,
                channelNumber,
                ...this.getCommonRequestParameters(commonHeaders),
            };
            const contentScreenDetailsOfferResponse = await this.getActionsApi(
                commonHeaders,
            ).contentScreenDetailsOfferGet(requestContentScreenDetailsParameters);
            return contentScreenDetailsOfferResponse.data;
        } catch (error) {
            Log.e(
                ActionsServiceRepositoryImpl.TAG,
                'getContentScreenDetailsOfferActionsFromService()',
                error,
            );
            throw error;
        }
    }

    private async getContentScreenDetailsContentActionsFromService(
        actionsParams: TActionsContentScreenContentIdParams,
    ): Promise<ContentScreenDetails> {
        try {
            const { contentId, collapsedVodItemId, contentSupplierPartnerId, partnerId } =
                actionsParams;
            const commonHeaders = new CommonHeaders(ScreenType.ActionScreenEpisodeItem);
            const requestContentScreenDetailsParameters: ContentScreenDetailsContentGetRequest = {
                contentId,
                collapsedVodItemId,
                contentSupplierPartnerId,
                partnerId,
                ...this.getCommonRequestParameters(commonHeaders),
            };
            const contentScreenDetailsContentIdResponse = await this.getActionsApi(
                commonHeaders,
            ).contentScreenDetailsContentGet(requestContentScreenDetailsParameters);
            return contentScreenDetailsContentIdResponse.data;
        } catch (error) {
            Log.e(
                ActionsServiceRepositoryImpl.TAG,
                'getContentScreenDetailsContentActionsFromService()',
                error,
            );
            throw error;
        }
    }

    private async getOfferActionsFromService(actionsParams: TActionsParams): Promise<TActionList> {
        try {
            const { offerId, groups, channelNumber, screenType, actionsNetworkCallId } =
                actionsParams;
            const commonHeaders = new CommonHeaders(screenType);
            const requestOfferActionsParameters: OfferActionsGetRequest = {
                offerId,
                groups,
                channelNumber,
                ...this.getCommonRequestParameters(commonHeaders),
            };

            const actionsApiResponseObj = await this.getActionsApi(commonHeaders).offerActionsGet(
                requestOfferActionsParameters,
            );
            const actionsApiResponse: TActionList = {
                actionsNetworkCallId,
                actionList: actionsApiResponseObj.data as TCustomActionList,
            };
            return actionsApiResponse;
        } catch (error: any) {
            error.actionsNetworkCallId = actionsParams.actionsNetworkCallId;
            Log.e(ActionsServiceRepositoryImpl.TAG, 'getOfferActionsFromService()', error);
            throw error;
        }
    }

    private async getUnifiedCollectionActionsFromService(
        actionsParams: TActionsUnifiedCollectionParams,
    ): Promise<TActionList> {
        try {
            const {
                unifiedCollectionId,
                partnerId,
                contentSupplierPartnerId,
                collapsedVodItemId,
                groups,
                screenType,
                actionsNetworkCallId,
                stationId,
            } = actionsParams;
            const commonHeaders = new CommonHeaders(screenType);
            const actionsApi = new ActionTagApi(
                new Configuration(this.getConfigurationParameters(commonHeaders)),
            );
            const reqParameters: UnifiedCollectionActionsGetRequest = {
                unifiedCollectionId,
                partnerId,
                contentSupplierPartnerId,
                collapsedVodItemId,
                groups,
                stationId,
                ...this.getCommonRequestParameters(commonHeaders),
            };
            const actionsApiResponseObj =
                await actionsApi.unifiedCollectionActionsGet(reqParameters);
            const actionsApiResponse: TActionList = {
                actionsNetworkCallId,
                actionList: actionsApiResponseObj.data as TCustomActionList,
            };
            return actionsApiResponse;
        } catch (error: any) {
            Log.e(
                ActionsServiceRepositoryImpl.TAG,
                'getUnifiedCollectionActionsFromService()',
                error,
            );
            throw error;
        }
    }

    private async getUnifiedCollectionScreenDetailsActionsFromService(
        actionsParams: TActionsParamsUCScreenDetails,
    ): Promise<TUnifiedCollectionScreenDetails> {
        try {
            const { unifiedCollectionId, partnerId, contentSupplierPartnerId } = actionsParams;
            const commonHeaders = new CommonHeaders(ScreenType.SeriesScreen);
            const requestClDetailsActionsParameters: UnifiedCollectionScreenDetailsGetRequest = {
                unifiedCollectionId,
                partnerId,
                contentSupplierPartnerId,
                ...this.getCommonRequestParameters(commonHeaders),
            };

            const unifiedCollectionScreenDetailsResponse = await this.getActionsApi(
                commonHeaders,
            ).unifiedCollectionScreenDetailsGet(requestClDetailsActionsParameters);
            return unifiedCollectionScreenDetailsResponse.data as TUnifiedCollectionScreenDetails;
        } catch (error: any) {
            Log.e(
                ActionsServiceRepositoryImpl.TAG,
                'getUnifiedCollectionScreenDetailsActionsFromService()',
                error,
            );
            throw error;
        }
    }

    private async getContentActionsFromService(
        actionsParams: TActionsContentParams,
    ): Promise<TActionList> {
        try {
            const {
                contentId,
                contentSupplierPartnerId,
                collapsedVodItemId,
                partnerId,
                stationId,
                channelNumber,
                groups,
                screenType,
                actionsNetworkCallId,
            } = actionsParams;
            const commonHeaders = new CommonHeaders(screenType);
            const requestContentActionsParameters: ContentActionsGetRequest = {
                contentId,
                contentSupplierPartnerId,
                collapsedVodItemId,
                partnerId,
                stationId,
                groups,
                channelNumber,
                ...this.getCommonRequestParameters(commonHeaders),
            };

            const actionsApiResponseObj = await this.getActionsApi(commonHeaders).contentActionsGet(
                requestContentActionsParameters,
            );
            const actionsApiResponse: TActionList = {
                actionsNetworkCallId,
                actionList: actionsApiResponseObj.data as TCustomActionList,
            };
            return actionsApiResponse;
        } catch (error: any) {
            Log.e(ActionsServiceRepositoryImpl.TAG, 'getContentActionFromService()', error);
            throw error;
        }
    }

    private async getPlayNextContentActionsFromService(
        actionsContentParams: TPlayNextContentActionsParams,
    ): Promise<TActionList> {
        const {
            contentId,
            contentSupplierPartnerId,
            collapsedVodItemId,
            screenType,
            partnerId,
            actionsNetworkCallId,
        } = actionsContentParams;
        const commonHeaders = new CommonHeaders(screenType);
        const requestPlayNextContentActionsParameters: PlayNextContentActionsGetRequest = {
            contentId,
            partnerId,
            contentSupplierPartnerId,
            collapsedVodItemId,
            ...this.getCommonRequestParameters(commonHeaders),
        };
        const actionsApiResponseObj = await this.getActionsApi(
            commonHeaders,
        ).playNextContentActionsGet(requestPlayNextContentActionsParameters);
        const actionsApiResponse: TActionList = {
            actionsNetworkCallId,
            actionList: actionsApiResponseObj.data as TCustomActionList,
        };
        return actionsApiResponse;
    }

    private async getPlayNextRecordingActionsFromService(
        playNextRecordingActionsParams: TPlayNextRecordingActionsParams,
    ): Promise<TActionList> {
        const { recordingId, channelNumber, screenType, actionsNetworkCallId } =
            playNextRecordingActionsParams;
        const commonHeaders = new CommonHeaders(screenType);
        const requestPlayNextRecordingActionsParameters: PlayNextRecordingActionsGetRequest = {
            recordingId,
            channelNumber,
            ...this.getCommonRequestParameters(commonHeaders),
        };
        const actionsApiResponseObj = await this.getActionsApi(
            commonHeaders,
        ).playNextRecordingActionsGet(requestPlayNextRecordingActionsParameters);
        const actionsApiResponse: TActionList = {
            actionsNetworkCallId,
            actionList: actionsApiResponseObj.data as TCustomActionList,
        };
        return actionsApiResponse;
    }

    /* @inheritdoc
     */
    public async fetchContentScreenDetailsContentActions(
        actionsParams: TActionsContentScreenContentIdParams,
    ): Promise<ContentScreenDetails> {
        try {
            return await this.getContentScreenDetailsContentActionsFromService(actionsParams);
        } catch (error) {
            Log.e(ActionsServiceRepositoryImpl.TAG, 'error', error);
            throw error;
        }
    }

    /**
     * @inheritdoc
     */
    public async fetchContentScreenDetailsOfferActions(
        actionsParams: TActionsContentScreenOfferParams,
    ): Promise<ContentScreenDetails> {
        try {
            return await this.getContentScreenDetailsOfferActionsFromService(actionsParams);
        } catch (error) {
            Log.e(ActionsServiceRepositoryImpl.TAG, 'error', error);
            throw error;
        }
    }

    /**
     * @inheritdoc
     */
    public async fetchContentScreenDetailsRecordingActions(
        actionsParams: TActionsContentScreenRecordingParams,
    ): Promise<ContentScreenDetails> {
        try {
            return await this.getContentScreenDetailsRecordingActionsFromService(actionsParams);
        } catch (error) {
            Log.e(ActionsServiceRepositoryImpl.TAG, 'error', error);
            throw error;
        }
    }

    /**
     * @inheritdoc
     */
    public async fetchOfferActions(actionsParams: TActionsParams): Promise<TActionList> {
        try {
            return await this.getOfferActionsFromService(actionsParams);
        } catch (error) {
            Log.e(ActionsServiceRepositoryImpl.TAG, 'error', error);
            throw error;
        }
    }

    /**
     * @inheritdoc
     */
    public fetchRecordingActions(actionsParams: TActionsRecordingParams): Promise<TActionList> {
        throw new Error('Method not implemented.');
    }

    /**
     * @inheritdoc
     */
    public fetchVodOfferActions(actionsParams: TActionsVodOfferParams): Promise<TActionList> {
        throw new Error('Method not implemented.');
    }

    /**
     * @inheritdoc
     */
    public fetchContentScreenDetailsVodOfferActions(actionParams: {
        vodOfferId: string;
        networkCallId?: number;
    }): Promise<ContentScreenDetails> {
        throw new Error('Method not implemented.');
    }

    /**
     * @inheritdoc
     */
    public async fetchUnifiedCollectionActions(
        actionsParams: TActionsUnifiedCollectionParams,
    ): Promise<TActionList> {
        try {
            return await this.getUnifiedCollectionActionsFromService(actionsParams);
        } catch (error) {
            Log.e(ActionsServiceRepositoryImpl.TAG, 'error', error);
            throw error;
        }
    }

    /**
     * @inheritdoc
     */
    public async fetchUnifiedCollectionScreenDetailsActions(
        actionsParams: TActionsParamsUCScreenDetails,
    ): Promise<TUnifiedCollectionScreenDetails> {
        try {
            return await this.getUnifiedCollectionScreenDetailsActionsFromService(actionsParams);
        } catch (error) {
            Log.e(ActionsServiceRepositoryImpl.TAG, 'error', error);
            throw error;
        }
    }

    /**
     * @inheritdoc
     */
    public async fetchContentActions(actionsParams: TActionsContentParams): Promise<TActionList> {
        try {
            return await this.getContentActionsFromService(actionsParams);
        } catch (error) {
            Log.e(ActionsServiceRepositoryImpl.TAG, 'error', error);
            throw error;
        }
    }

    /**
     * @inheritdoc
     */
    public async fetchPlayNextContentActions(
        playNextContentActionsParams: TPlayNextContentActionsParams,
    ): Promise<TActionList> {
        try {
            return await this.getPlayNextContentActionsFromService(playNextContentActionsParams);
        } catch (error) {
            Log.e(ActionsServiceRepositoryImpl.TAG, 'error', error);
            throw error;
        }
    }

    /**
     * @inheritdoc
     */
    public async fetchPlayNextRecordingActions(
        playNextRecordingActionsParams: TPlayNextRecordingActionsParams,
    ): Promise<TActionList> {
        try {
            return await this.getPlayNextRecordingActionsFromService(
                playNextRecordingActionsParams,
            );
        } catch (error) {
            Log.e(ActionsServiceRepositoryImpl.TAG, 'error', error);
            throw error;
        }
    }
}
